// src/components/ActionItemListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import ActionItemCard from './ActionItemCard';
import CreateEntityModal from './CreateEntityModal';
import { ACTION_ITEM_STATUS_OPTIONS } from '../constants';
import { usePersistedFilters, Tooltip, SingleSelectDropdown } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { ROUTES } from '../constants/routes';
import { useEntityNavigationContext } from './EntityNavigation';
import { RiInformationLine, RiAddLine } from 'react-icons/ri';
import { useStaffContext } from '../contexts/StaffContext';

const ActionItemListView = () => {
    const { apiService, clientId, dateRange, setDateRange } = useOutletContext();
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';

    const [pendingActionItemsList, setPendingActionItemsList] = useState([]);
    const [loadingActionItems, setLoadingActionItems] = useState(false);
    const [errorActionItems, setErrorActionItems] = useState(null);

    
    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(false);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);

    const [actionItemInitiativeFilter, setActionItemInitiativeFilter] = useState('');
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    const [loadingInitiativesForFilter, setLoadingInitiativesForFilter] = useState(false);
    
    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [initiativesList, setInitiativesList] = useState([]);
    
    // Deadline filter - can be set from dashboard card for overdue items
    const [deadlineBeforeFilter, setDeadlineBeforeFilter] = usePersistedFilters('deadlineBeforeFilter', '');
    
    // Action item status filter
    const [actionItemStatusFilter, setActionItemStatusFilter] = usePersistedFilters('actionItemStatusFilter', '');
    
    // Today's date for deadline filter options
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const hasActiveFilters = selectedDris.length > 0 || actionItemInitiativeFilter !== '' || deadlineBeforeFilter !== '' || actionItemStatusFilter !== '';
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setActionItemInitiativeFilter('');
        setDeadlineBeforeFilter('');
        setActionItemStatusFilter('');
    };

    const loadFilterAndDriOptions = useCallback(async () => {
        if (!apiService || !clientId) return;
        setLoadingDris(true); setLoadingInitiativesForFilter(true);
        setDris([]); setInitiativeListForFilter([]);
        try {
            const [driResults, initiativeResults, staffResults, fullInitiatives] = await Promise.all([
                 apiService.getClientDistinctDRIs(clientId),
                 apiService.getClientDistinctInitiatives(clientId),
                 apiService.getAllStaff(false).catch(() => []),
                 apiService.getClientInitiatives(clientId).catch(() => [])
            ]);
            const staffNames = staffResults.map(s => s.staffName || s.name).filter(Boolean);
            // Prefer full staff list; fallback to existing DRIs
            setDris(staffNames.length > 0 ? staffNames : driResults.map(r => r.dri).filter(Boolean));
            setInitiativeListForFilter(initiativeResults.map(r => r.initiativeName).filter(Boolean));
            // Staff list for create modal
            setStaffList(staffResults.map(s => s.staffName || s.name).filter(Boolean));
            // Full initiatives list for create modal (with id and name)
            setInitiativesList(fullInitiatives.map(i => ({ id: i.initiativeId || i.id, name: i.initiativeName || i.name })).filter(i => i.id && i.name));
        } catch (err) { console.error("Error loading filter options:", err); }
        finally { setLoadingDris(false); setLoadingInitiativesForFilter(false); }
    }, [apiService, clientId]);

    // Filter action items by deadline (status is now filtered server-side)
    const filteredActionItems = useMemo(() => {
        if (!deadlineBeforeFilter) return pendingActionItemsList;
        return pendingActionItemsList.filter(item => {
            if (!item.deadline) return false;
            return item.deadline < deadlineBeforeFilter;
        });
    }, [pendingActionItemsList, deadlineBeforeFilter]);

    const actionItemStatusLabel = actionItemStatusFilter
        ? `${actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1)} Items`
        : 'Action Items';

    // Compute assignee chart data from the filtered action items (includes deadline filter)
    const actionItemsByAssignee = useMemo(() => {
        if (!filteredActionItems || filteredActionItems.length === 0) return [];
        
        // Group by assignee display name (convert emails to names) and count
        const assigneeCounts = {};
        filteredActionItems.forEach(item => {
            const candidateAssignee = item.assignee || item.assigned_to;
            const rawAssignee = candidateAssignee && candidateAssignee.trim() ? candidateAssignee : null;
            const displayName = rawAssignee ? getDriDisplayName(rawAssignee) : 'Unassigned';
            assigneeCounts[displayName] = (assigneeCounts[displayName] || 0) + 1;
        });
        
        // Convert to array format for chart
        return Object.entries(assigneeCounts)
            .map(([assignee, count]) => ({ assignee, count }))
            .sort((a, b) => b.count - a.count);
    }, [filteredActionItems, getDriDisplayName]);

    useEffect(() => {
        if (clientId) {
            loadFilterAndDriOptions();
        }
    }, [clientId, loadFilterAndDriOptions]);

    useEffect(() => {
        const loadPendingActionItemsList = async () => {
            if (!apiService || !clientId || !dateRange || !dateRange.start || !dateRange.end) return;
            setLoadingActionItems(true); setErrorActionItems(null);
            try {
                const items = await apiService.getAllPendingActionItemsList(
                    clientId, 
                    selectedDris.length > 0 ? selectedDris : null,
                    actionItemInitiativeFilter || null,
                    dateRange.start,
                    dateRange.end,
                    actionItemStatusFilter || null
                );
                setPendingActionItemsList(items);
            } catch (err) { setErrorActionItems(err.message); setPendingActionItemsList([]); }
            finally { setLoadingActionItems(false); }
        };
        
        loadPendingActionItemsList();
    }, [apiService, clientId, selectedDris, actionItemInitiativeFilter, dateRange, actionItemStatusFilter]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleActionItemUpdate = (itemId, updatedField, newValue) => {
        setPendingActionItemsList(prevList => 
            prevList.map(item => 
                item.id === itemId ? { ...item, [updatedField]: newValue } : item
            )
        );
    };

    const handleActionItemStatusChange = async (actionItemId, newStatus) => {
        try {
            await apiService.updateNodeStatus(actionItemId, newStatus);
            handleActionItemUpdate(actionItemId, 'status', newStatus);
        } catch (err) {
            console.error('Error updating action item status:', err);
        }
    };

    const handleActionItemDeadlineChange = async (actionItemId, newDeadline) => {
        try {
            await apiService.updateNodeProperty(actionItemId, 'deadline', newDeadline);
            handleActionItemUpdate(actionItemId, 'deadline', newDeadline);
        } catch (err) {
            console.error('Error updating action item deadline:', err);
        }
    };

    const handleActionItemAssigneeChange = async (actionItemId, newAssignee) => {
        try {
            await apiService.updateNodeProperty(actionItemId, 'assigned_to', newAssignee);
            handleActionItemUpdate(actionItemId, 'assignee', newAssignee);
            handleActionItemUpdate(actionItemId, 'assigned_to', newAssignee);
        } catch (err) {
            console.error('Error updating action item assignee:', err);
        }
    };

    const handleActionItemCreated = (newItem) => {
        // Add the new item to the list
        setPendingActionItemsList(prev => [newItem, ...prev]);
    };

    return (
        <>
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} loadingDris={loadingDris} driDisplayFormatter={getDriDisplayName}
                filterGroups={[{ id: 'actions', label: 'Action Items', filters: [{ key: 'deadline' }, { key: 'initiative' }, { key: 'actionStatus' }] }]}
                filters={{ deadline: deadlineBeforeFilter, initiative: actionItemInitiativeFilter, actionStatus: actionItemStatusFilter }}
                filterConfigs={{
                    deadline: { label: 'Deadline', render: () => <SingleSelectDropdown id="deadlineFilter" label="Deadline" value={deadlineBeforeFilter === today ? 'overdue' : (deadlineBeforeFilter || '')} onChange={(val) => setDeadlineBeforeFilter(val === 'overdue' ? today : '')} options={['overdue']} allLabel="All" capitalize={true} /> },
                    initiative: { label: 'Initiative', render: () => <SingleSelectDropdown id="initiativeFilter" label="Initiative" value={actionItemInitiativeFilter} onChange={(val) => setActionItemInitiativeFilter(val)} options={initiativeListForFilter} allLabel="All" loading={loadingInitiativesForFilter} capitalize={false} /> },
                    actionStatus: { label: 'Status', render: () => <SingleSelectDropdown id="actionStatusFilter" label="Status" value={actionItemStatusFilter} onChange={(val) => setActionItemStatusFilter(val)} options={ACTION_ITEM_STATUS_OPTIONS} allLabel="All" capitalize={true} /> }
                }}
                activeFilterChips={[
                    ...(deadlineBeforeFilter ? [{ label: 'Deadline', value: 'Overdue', onRemove: () => setDeadlineBeforeFilter('') }] : []),
                    ...(actionItemInitiativeFilter ? [{ label: 'Initiative', value: actionItemInitiativeFilter, onRemove: () => setActionItemInitiativeFilter('') }] : []),
                    ...(actionItemStatusFilter ? [{ label: 'Status', value: actionItemStatusFilter, onRemove: () => setActionItemStatusFilter('') }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />

            <div className="rounded-xl shadow p-4 md:p-6 mb-6 md:mb-8" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }} data-testid="assignee-chart">
                <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--pallium-text-primary)' }}>
                    {`${actionItemStatusLabel} by Assignee`}
                </h3>
                {loadingActionItems ? (
                    <p className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>Loading chart...</p>
                ) : actionItemsByAssignee.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(200, actionItemsByAssignee.length * 28 + 40)}>
                        <BarChart data={actionItemsByAssignee} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--pallium-stroke-light)" />
                            <XAxis type="number" allowDecimals={false} tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} />
                            <YAxis 
                                dataKey="assignee" 
                                type="category" 
                                width={180} 
                                tick={{fontSize: 10, fill: 'var(--pallium-text-secondary)'}} 
                                interval={0}
                                tickFormatter={(value) => value.length > 28 ? value.substring(0, 25) + '...' : value}
                            />
                            <RechartsTooltip 
                                wrapperClassName="text-xs"
                                formatter={(value) => [value, actionItemStatusLabel]}
                                labelFormatter={(label) => label}
                                contentStyle={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)', borderRadius: '8px' }}
                            />
                            <Bar dataKey="count" fill="var(--pallium-primary-purple)" barSize={18} name="Pending Items" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-center text-sm py-4" style={{ color: 'var(--pallium-icon-gray)' }}>
                        No {actionItemStatusFilter ? actionItemStatusFilter : ''} action items found to display chart.
                    </p>
                )}
             </div>

            {/* Action Items Card Grid */}
            <div className="rounded-xl shadow p-4 md:p-6" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }} data-testid="action-list">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>
                            {deadlineBeforeFilter 
                                ? "Overdue Action Items" 
                                : actionItemStatusFilter 
                                    ? `${actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1)} Action Items`
                                    : "Action Items"}
                        </h3>
                    <Tooltip content="Action items include newly created items without communications, plus items linked to initiatives with communications in the selected date range.">
                            <RiInformationLine className="w-4 h-4 cursor-help" style={{ color: 'var(--pallium-icon-gray)' }} />
                        </Tooltip>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white transition-colors"
                        style={{ backgroundColor: 'var(--pallium-success-green)' }}
                    >
                        <RiAddLine className="w-4 h-4" />
                        New Action Item
                    </button>
                </div>
                <p className="text-xs mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Action items from initiatives mentioned in communications, plus newly created items without communications
                </p>
                
                {loadingActionItems ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>Loading action items...</div>
                ) : errorActionItems ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-error-red)' }}>Error loading action items: {errorActionItems}</div>
                ) : filteredActionItems.length === 0 ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--pallium-icon-gray)' }}>
                        No {deadlineBeforeFilter ? 'overdue' : actionItemStatusFilter ? actionItemStatusFilter : ''} action items found.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredActionItems.map((actionItem, index) => (
                            <ActionItemCard
                                key={actionItem.id || index}
                                data-testid="action-card"
                                actionItem={actionItem}
                                clientId={clientId}
                                onClick={() => navigate(`${ROUTES.ACCOUNTS}/${clientId}/action-items/${actionItem.id}`)}
                                editable={true}
                                statusOptions={ACTION_ITEM_STATUS_OPTIONS}
                                assigneeOptions={dris}
                                onStatusChange={handleActionItemStatusChange}
                                onDeadlineChange={handleActionItemDeadlineChange}
                                onAssigneeChange={handleActionItemAssigneeChange}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Create Action Item Modal */}
            <CreateEntityModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                entityType="action-item"
                clientId={clientId}
                apiService={apiService}
                onSuccess={handleActionItemCreated}
                staffList={staffList}
                initiativesList={initiativesList}
            />
        </>
    );
};

export default ActionItemListView;